/**
 * Filter Dropdown Component
 * 
 * Multi-select filter dropdown
 * 
 * Version: 6.2.0
 */
import React, { useState, useRef, useEffect, ReactNode } from 'react'
import './FilterDropdown.css'

interface FilterOption {
  id: string
  label: string
  icon?: ReactNode
  count?: number
}

interface FilterDropdownProps {
  label: string
  options: FilterOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  multiple?: boolean
  searchable?: boolean
  className?: string
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  options,
  selected,
  onChange,
  placeholder = 'Select...',
  multiple = true,
  searchable = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus search when opened
  useEffect(() => {
    if (isOpen && searchable && searchRef.current) {
      searchRef.current.focus()
    }
  }, [isOpen, searchable])

  const handleToggle = () => {
    setIsOpen(!isOpen)
    if (!isOpen) setSearchTerm('')
  }

  const handleOptionClick = (optionId: string) => {
    if (multiple) {
      if (selected.includes(optionId)) {
        onChange(selected.filter(id => id !== optionId))
      } else {
        onChange([...selected, optionId])
      }
    } else {
      onChange([optionId])
      setIsOpen(false)
    }
  }

  const handleClear = () => {
    onChange([])
  }

  const filteredOptions = options.filter(opt => 
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const selectedOptions = options.filter(opt => selected.includes(opt.id))
  const displayText = selectedOptions.length > 0
    ? selectedOptions.map(opt => opt.label).join(', ')
    : placeholder

  return (
    <div ref={containerRef} className={`filter-dropdown-container ${className}`}>
      <button 
        className={`filter-dropdown-trigger ${isOpen ? 'open' : ''} ${selected.length > 0 ? 'has-selection' : ''}`}
        onClick={handleToggle}
        type="button"
      >
        <span className="filter-label">{label}</span>
        {selected.length > 0 && (
          <span className="filter-count">{selected.length}</span>
        )}
        <span className="filter-value">{displayText}</span>
        <svg className="filter-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {isOpen && (
        <div className="filter-dropdown-menu">
          {searchable && (
            <div className="filter-search">
              <input
                ref={searchRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="filter-search-input"
              />
            </div>
          )}

          <div className="filter-options">
            {filteredOptions.length === 0 ? (
              <div className="filter-empty">No options found</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.id}
                  className={`filter-option ${selected.includes(option.id) ? 'selected' : ''}`}
                  onClick={() => handleOptionClick(option.id)}
                  type="button"
                >
                  {multiple && (
                    <span className={`option-checkbox ${selected.includes(option.id) ? 'checked' : ''}`}>
                      {selected.includes(option.id) && '✓'}
                    </span>
                  )}
                  {option.icon && <span className="option-icon">{option.icon}</span>}
                  <span className="option-label">{option.label}</span>
                  {option.count !== undefined && (
                    <span className="option-count">{option.count}</span>
                  )}
                </button>
              ))
            )}
          </div>

          {selected.length > 0 && (
            <div className="filter-footer">
              <button className="filter-clear" onClick={handleClear} type="button">
                Clear All
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default FilterDropdown